export const createValueLabelsPlugin = ({
  // Formato del texto (€, números)
  format = (v) => `${Number(v).toFixed(2)} €`,

  // Estilo
  font = 'bold 14px Arial',
  textColor = '#000000',

  // Comportamiento
  minHeightForInside = 30, // si la barra es menor, no cabe el texto
  minHeightWhenOneOutside = 45, // altura mínima cuando una barra ya va fuera
  gap = 4,                // espacio entre cajas apiladas
  offsetTop = 10,         // separación respecto a la barra

  // Orden cuando ambas barras son pequeñas
  // (por defecto: dataset 1 arriba, dataset 0 abajo)
  order = (a, b) => b.di - a.di
} = {}) => ({
  id: 'valueLabels',

  afterDatasetsDraw(chart) {
    const ctx = chart.ctx;
    ctx.save();

    ctx.textAlign = 'center';
    ctx.font = font;
    ctx.textBaseline = 'middle';

    const getXYBase = (el) => {
      if (!el) return null;

      const m = el._model || el._view;
      if (!m) return null;

      return { x: m.x, y: m.y, base: m.base };
    };

    const drawBoxLabel = (x, yCenter, text, borderColor) => {
      const paddingX = 6;
      const paddingY = 4;
      const metrics = ctx.measureText(text);
      const textWidth = metrics.width;
      const textHeight = 14;

      const rectW = textWidth + paddingX * 2;
      const rectH = textHeight + paddingY * 2;
      const rectX = x - rectW / 2;
      const rectY = yCenter - rectH / 2;

      ctx.strokeStyle = borderColor;
      ctx.lineWidth = 1.5;
      ctx.strokeRect(rectX, rectY, rectW, rectH);

      ctx.fillStyle = textColor;
      ctx.fillText(text, x, yCenter);

      return rectH;
    };

    const datasets = chart.data.datasets || [];
    const labelsCount = (chart.data.labels || []).length;

    for (let i = 0; i < labelsCount; i++) {
      const metas = datasets.map((_, di) => chart.getDatasetMeta(di));
      const elements = metas.map(m => (m && !m.hidden ? m.data[i] : null));
      if (!elements.some(Boolean)) continue;

      const props = elements.map(getXYBase);
      const first = props.find(p => p);
      if (!first) continue;

      const x = first.x;

      const yTops = props
        .filter(Boolean)
        .map(p => Math.min(p.y, p.base));
      if (!yTops.length) continue;

      const topOfStack = Math.min.apply(null, yTops);

      // 1. determinar qué barras son pequeñas con umbral base
      const initialInfo = datasets.map((ds, di) => {
        const meta = metas[di];
        const p = props[di];
        if (!meta || meta.hidden || !p) return null;

        const v = ds.data[i];
        if (v == null || Number(v) === 0) return null;

        const barHeight = Math.abs(p.base - p.y);
        const smallWithBaseThreshold = barHeight < minHeightForInside;

        const barColor = Array.isArray(ds.backgroundColor)
          ? ds.backgroundColor[i]
          : ds.backgroundColor || ds.borderColor;

        return {
          di,
          text: format(v),
          barHeight,
          smallWithBaseThreshold,
          barColor,
          y: p.y,
          base: p.base
        };
      }).filter(Boolean);

      if (!initialInfo.length) continue;

      // Contar cuántas barras irían fuera con el umbral base
      const countSmallWithBase = initialInfo.filter(d => d.smallWithBaseThreshold).length;

      // 2. si hay exactamente 1 barra pequeña, aumentar umbral para las demás
      const info = initialInfo.map(d => {
        let small;
        
        if (countSmallWithBase === 1) {
          // Si ya hay una barra que va fuera, usar umbral más alto para las demás
          if (d.smallWithBaseThreshold) {
            small = true;
          } else {
            // Para las otras, usar umbral más alto
            small = d.barHeight < minHeightWhenOneOutside;
          }
        } else {
          // Si no hay ninguna fuera o ya hay 2+ fuera, usar umbral normal
          small = d.smallWithBaseThreshold;
        }

        return {
          ...d,
          small
        };
      });

      const outside = info.filter(d => d.small);
      const inside = info.filter(d => !d.small);

      // Si 2 o más labels van fuera -> apilar fuera SIEMPRE
      if (outside.length >= 2) {
        const ordered = outside.slice().sort(order);

        const boxHeights = ordered.map(() => 14 + 4 * 2);
        const totalStackHeight =
            boxHeights.reduce((a, b) => a + b, 0) +
            gap * (ordered.length - 1);

        const bottomEdgeY = topOfStack - offsetTop;
        let currentY =
            (bottomEdgeY - totalStackHeight) + (boxHeights[0] / 2);

        ordered.forEach((d, idx) => {
            drawBoxLabel(x, currentY, d.text, d.barColor);
            currentY += boxHeights[idx] + gap;
        });

        // los que caben dentro, dentro
        inside.forEach(d => {
            const barMid = (d.y + d.base) / 2;
            ctx.fillStyle = textColor;
            ctx.fillText(d.text, x, barMid);
        });

      } else {
        // Caso normal: 0 o 1 fuera
        info.forEach(d => {
            const barTop = Math.min(d.y, d.base);
            const barMid = (d.y + d.base) / 2;

            if (!d.small) {
              ctx.fillStyle = textColor;
              ctx.fillText(d.text, x, barMid);
            } else {
              drawBoxLabel(x, barTop - offsetTop, d.text, d.barColor);
            }
        });
      }
    }

    ctx.restore();
  }
});